import React, { useEffect, useState, useCallback } from 'react';
import { BlockMath, InlineMath } from 'react-katex';
import 'katex/dist/katex.min.css';
import { Calculator, Trash2, Plus, X } from 'lucide-react';
import { classroomApi } from '@/lib/classroomApi';
import { toast } from 'sonner';

/**
 * Phase 3A-2: Math notes panel.
 *
 *  - Both roles can add notes (LaTeX); tutor sees a Delete on every note,
 *    students only on their own.
 *  - Live preview while typing.
 *  - New notes broadcast via Daily app-message 'wh-math-refresh' so the
 *    counterpart's panel auto-refreshes.
 */
const EXAMPLES = [
  { label: 'Pythagoras', latex: 'a^2 + b^2 = c^2' },
  { label: 'Quadratic', latex: 'x = \\frac{-b \\pm \\sqrt{b^2 - 4ac}}{2a}' },
  { label: 'Integral', latex: '\\int_{a}^{b} f(x)\\,dx' },
  { label: 'Limit', latex: '\\lim_{x \\to 0} \\frac{\\sin x}{x} = 1' },
];

const SafeBlock = ({ latex }) => {
  try {
    return <BlockMath math={latex} />;
  } catch (_e) {
    return <div className="text-xs text-red-600">Invalid LaTeX</div>;
  }
};

const MathNotesPanel = ({ roomId, token, role, callObject, enabled = true }) => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [draftLatex, setDraftLatex] = useState('');
  const [draftLabel, setDraftLabel] = useState('');
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(async () => {
    try { const r = await classroomApi.mathNotesList(roomId, token); setItems(r.items || []); }
    catch (_e) {}
  }, [roomId, token]);

  useEffect(() => { if (enabled) refresh(); }, [refresh, enabled]);

  useEffect(() => {
    if (!callObject || !enabled) return;
    const handler = (e) => {
      const m = e?.data; if (!m) return;
      if (m.kind === 'wh-math-refresh') refresh();
    };
    callObject.on('app-message', handler);
    return () => { try { callObject.off('app-message', handler); } catch (_e) {} };
  }, [callObject, refresh, enabled]);

  if (!enabled) return null;

  const save = async () => {
    if (!draftLatex.trim()) return;
    setSaving(true);
    try {
      const note = await classroomApi.mathNoteCreate(roomId, token, draftLatex.trim(), draftLabel.trim());
      setItems((it) => [note, ...it]);
      setDraftLatex(''); setDraftLabel('');
      try { callObject?.sendAppMessage({ kind: 'wh-math-refresh' }); } catch (_e) {}
      toast.success('Math note saved');
    } catch (e) { toast.error(e.message || 'Could not save'); }
    finally { setSaving(false); }
  };

  const remove = async (noteId) => {
    if (!window.confirm('Delete this math note?')) return;
    try {
      await classroomApi.mathNoteDelete(roomId, token, noteId);
      setItems((it) => it.filter((n) => n.note_id !== noteId));
      try { callObject?.sendAppMessage({ kind: 'wh-math-refresh' }); } catch (_e) {}
    } catch (e) { toast.error(e.message || 'Could not delete'); }
  };

  const canDelete = (n) => role === 'tutor' || n.author_role === role;

  return (
    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden" data-testid="classroom-math-notes">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full px-4 py-3 flex items-center justify-between border-b border-gray-100 hover:bg-gray-50 transition"
        data-testid="math-notes-toggle"
      >
        <div className="flex items-center gap-2">
          <Calculator className="h-4 w-4 text-red-700" />
          <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Math Notes</div>
          {items.length > 0 && (
            <span className="text-[10px] bg-red-700 text-white rounded-full px-2 py-0.5 font-bold">{items.length}</span>
          )}
        </div>
        <span className="text-[10px] text-gray-400 uppercase tracking-wider">{open ? 'Hide' : 'Show'}</span>
      </button>

      {open && (
        <div className="p-4 space-y-4">
          <div className="space-y-2">
            <input
              type="text" value={draftLabel} onChange={(e) => setDraftLabel(e.target.value)}
              placeholder="Optional label (e.g. 'Question 4')"
              className="w-full h-9 px-3 rounded-lg border border-gray-200 text-xs focus:outline-none focus:ring-2 focus:ring-red-200"
              data-testid="math-notes-label"
            />
            <textarea
              value={draftLatex} onChange={(e) => setDraftLatex(e.target.value)}
              rows={3}
              placeholder="Type LaTeX, e.g.  \frac{a}{b} = c^2"
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-red-200"
              data-testid="math-notes-latex"
            />
            {draftLatex && (
              <div className="bg-gray-50 border border-gray-100 rounded-lg p-3 overflow-x-auto" data-testid="math-notes-preview">
                <SafeBlock latex={draftLatex} />
              </div>
            )}
            <div className="flex items-center gap-2 flex-wrap">
              {EXAMPLES.map((ex) => (
                <button key={ex.label} onClick={() => setDraftLatex(ex.latex)}
                  className="h-7 px-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-[10px] uppercase tracking-wider font-bold text-gray-700"
                  data-testid={`math-example-${ex.label.toLowerCase()}`}>
                  {ex.label}
                </button>
              ))}
              <button onClick={save} disabled={!draftLatex.trim() || saving}
                className="ml-auto h-8 px-4 rounded-lg bg-red-700 hover:bg-red-800 text-white text-xs font-bold uppercase tracking-wider disabled:opacity-40 flex items-center gap-1"
                data-testid="math-notes-save">
                <Plus className="h-3 w-3" /> {saving ? 'Saving…' : 'Add note'}
              </button>
            </div>
          </div>

          <div className="space-y-2 max-h-72 overflow-y-auto" data-testid="math-notes-list">
            {items.length === 0 && (
              <div className="text-[11px] text-gray-400 italic text-center py-4">No math notes yet</div>
            )}
            {items.map((n) => (
              <div key={n.note_id} className="bg-gray-50 border border-gray-100 rounded-lg p-3 group" data-testid={`math-note-${n.note_id}`}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    {n.label && <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">{n.label}</div>}
                    <div className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${n.author_role === 'tutor' ? 'bg-red-700 text-white' : 'bg-teal-700 text-white'}`}>
                      {n.author_role}
                    </div>
                  </div>
                  {canDelete(n) && (
                    <button onClick={() => remove(n.note_id)}
                      className="opacity-0 group-hover:opacity-100 text-red-700 hover:bg-red-50 rounded p-1 transition"
                      data-testid={`math-note-delete-${n.note_id}`}>
                      <Trash2 className="h-3 w-3" />
                    </button>
                  )}
                </div>
                <div className="overflow-x-auto"><SafeBlock latex={n.latex} /></div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export const InlineMathSafe = ({ latex }) => {
  try { return <InlineMath math={latex} />; }
  catch (_e) { return <span className="text-red-600 text-xs">{latex}</span>; }
};

export default MathNotesPanel;
